import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";

import { $documentRequiredVars, type DocumentRequiredVarsStatus } from "./required-vars.atom";

// =============================================================================
// Types
// =============================================================================

export type DocumentOnboardingState = {
	/** Whether the user has dismissed the onboarding UI for this session. */
	dismissed: boolean;
	/** Per-environment resolution status for required env vars. */
	environmentStatus: DocumentRequiredVarsStatus;
	/** Whether there are any missing variables across all environments. */
	hasMissingVars: boolean;
};

// =============================================================================
// Dismissal (in-memory, resets on app restart)
// =============================================================================

const $onboardingDismissedInternal = atomFamily((_documentId: string) => atom(false));

export const $onboardingDismissed = atomFamily((documentId: string) =>
	atom(
		(get) => get($onboardingDismissedInternal(documentId)),
		(_get, set, dismissed: boolean) => {
			set($onboardingDismissedInternal(documentId), dismissed);
		}
	)
);

// =============================================================================
// Composite onboarding state
// =============================================================================

export const $documentOnboardingState = atomFamily((documentId: string) => {
	const stateAtom = atom(async (get): Promise<DocumentOnboardingState> => {
		const dismissed = get($onboardingDismissed(documentId));

		if (dismissed) {
			return {
				dismissed,
				environmentStatus: {},
				hasMissingVars: false,
			};
		}

		const environmentStatus = await get($documentRequiredVars(documentId));

		const hasMissingVars = Object.values(environmentStatus).some(
			(env) => env.missing.length > 0
		);

		return {
			dismissed,
			environmentStatus,
			hasMissingVars,
		};
	});
	stateAtom.debugLabel = `documentOnboardingState/${documentId}`;
	return stateAtom;
});

export const $loadableDocumentOnboardingState = atomFamily((documentId: string) =>
	loadable($documentOnboardingState(documentId))
);
